import {
  ICarbon,
  IGhGasEmissionValue,
  IGhGasEmissionVerify,
  IGhGasEmissionVolume,
  IGreenhouse,
  IGreenhouseGoal,
  IGreenhouseGoalTerm,
  IGreenhouseMasterData,
  IGreenhouseStandard,
  INetZero,
  IPerformanceGhGasMgmtReport,
  ITermTarget,
  ISubcommitteeResponsibility,
  IGreenhouseGasOther,
  IPerformanceGhGasMgmt,
} from '@Domains/Esg/EsgGreenhouse';
import { IRemark } from '@Domains/utils';
import { RemarkFormValues } from '@Utils/Types/FormValuesTypes';
import {
  ICarbonFormValues,
  IClimateChangeStructureFormValues,
  IGhGasEmissionVerifyFormValues,
  IGhGasEmissionVolumeFormValues,
  IGhGasScopeFormValues,
  IGreenhouseFormValues,
  IGreenhouseGoalFormValues,
  IGreenhouseGoalTermFormValues,
  IGreenhouseStandardFormValues,
  IGreenhouseStrategyFormValues,
  ISubcommitteeResponsibilityFormValues,
  IBoardResponsibilityFormValues,
  IGreenhouseGasOtherFormValues,
  IPerformanceGhGasMgmtFormValues,
} from '../EsgGreenhouseFormProvider/types';
import { CommitteeType, GoalType } from 'src/Enum/EsgGreenhouse';
import {
  INetZeroFormValues,
  IPerformanceGhGasMgmtReportFormValues,
  ITermTargetFormValues,
} from '../EsgGreenhouseFormProvider/types';
import { GREENHOUSE_STANDARD_LIST_ID } from '../GreenhouseStandard/GreenhouseStandardList';
import { mapAssets } from '@Utils/assetUtils';

export const mapEsgGreenhouseToPayload = (
  formValues: IGreenhouseFormValues
): IGreenhouse => {
  const {
    greenhouseStandard,
    greenhouseGoalTerm,
    greenhouseStrategy,
    climateChangeStructure,
    performanceGhGasMgmtReport,
    otherDescriptions,
  } = formValues;

  return {
    greenhouseStandard:
      greenhouseStandard && mapStandardToPayload(greenhouseStandard),
    greenhouseGoalTerm:
      greenhouseGoalTerm && mapGreenhouseGoalTermToPayload(greenhouseGoalTerm),
    greenhouseStrategy:
      greenhouseStrategy && mapGhStrategyToPayload(greenhouseStrategy),
    climateChangeStructure:
      climateChangeStructure &&
      mapClimateChangeStructureToPayload(climateChangeStructure),
    performanceGhGasMgmtReport:
      performanceGhGasMgmtReport &&
      mapPerfGhGasToPayload(performanceGhGasMgmtReport),
    otherDescriptions,
  } as IGreenhouse;
};

const mapStandardToPayload = (
  standard: IGreenhouseStandardFormValues
): IGreenhouseStandard => {
  const { remark, principles = {}, ...values } = standard;
  const updatedPrinciples: IGreenhouseStandard['principles'] = [];

  Object.keys(principles).map((principle) => {
    const { checked, infoCategoryMasterDataId, ...principleValues } =
      principles[principle];
    const isOther =
      infoCategoryMasterDataId === GREENHOUSE_STANDARD_LIST_ID.OTHER;

    if (checked) {
      return updatedPrinciples.push({
        ...principleValues,
        infoCategoryMasterDataId,
        nameTh:
          isOther && principleValues.nameTh !== ''
            ? principleValues?.nameTh
            : null,
        nameEn:
          isOther && principleValues.nameEn !== ''
            ? principleValues?.nameEn
            : null,
      } as IGreenhouseMasterData);
    }
    return null;
  });

  return {
    ...values,
    principles: updatedPrinciples,
    remark: mapRemarkToPayload(remark),
  } as IGreenhouseStandard;
};

const mapPerfGhGasToPayload = (
  perfGhGasMgmt: IPerformanceGhGasMgmtReportFormValues
): IPerformanceGhGasMgmtReport => {
  const {
    remark,
    introduction,
    ghGasEmissionVerify,
    ghGasEmissionVolume,
    performanceGhGasMgmt,
    ...values
  } = perfGhGasMgmt;

  return {
    ...values,
    remark: mapRemarkToPayload(remark),
    introduction: mapIntroToPayload(introduction),
    ghGasEmissionVolume:
      ghGasEmissionVolume &&
      mapGhGasEmissionVolumeToPayload(ghGasEmissionVolume),
    ghGasEmissionVerify:
      ghGasEmissionVerify &&
      mapGhGasEmissionVerifyToPayload(ghGasEmissionVerify),
    performanceGhGasMgmt:
      performanceGhGasMgmt &&
      mapPerformanceGhGasMgmtToPayload(performanceGhGasMgmt),
  } as IPerformanceGhGasMgmtReport;
};

const mapGhGasEmissionVolumeToPayload = (
  ghGasEmissionVolume: IGhGasEmissionVolumeFormValues
) => {
  const {
    totalGhGasConsumption,
    ghGasScope1 = {},
    ghGasScope2 = {},
    ghGasScope3 = {},
    remarkTotalGhGas,
    remarkScope1,
    remarkScope2,
    remarkScope3,
    ...values
  } = ghGasEmissionVolume;
  return {
    ...values,
    remarkTotalGhGas: mapRemarkToPayload(remarkTotalGhGas),
    remarkScope1: mapRemarkToPayload(remarkScope1),
    remarkScope2: mapRemarkToPayload(remarkScope2),
    remarkScope3: mapRemarkToPayload(remarkScope3),
    totalGhGasConsumption: mapGhGasScopeValue(
      totalGhGasConsumption ?? {},
      'total'
    ),
    ghGasScope1: mapGhGasScopeValue(ghGasScope1),
    ghGasScope2: mapGhGasScopeValue(ghGasScope2),
    ghGasScope3: mapGhGasScopeValue(ghGasScope3),
  } as IGhGasEmissionVolume;
};

const mapGhGasScopeValue = (
  gasScope: IGhGasScopeFormValues,
  type?: 'total' | 'byScope'
) => {
  const { asOfYear, asOfYesteryear, asOfYearBeforeYesteryear, ...values } =
    gasScope;
  let newGasScope = null;

  if (!asOfYear && !asOfYesteryear && !asOfYearBeforeYesteryear) {
    if (type === 'total') {
      newGasScope = {
        ...values,
        asOfYear: 0,
        asOfYesteryear: 0,
        asOfYearBeforeYesteryear: 0,
      };
    } else return null;
  } else {
    newGasScope = {
      ...values,
      asOfYear,
      asOfYesteryear,
      asOfYearBeforeYesteryear,
    };
  }
  return newGasScope as IGhGasEmissionValue;
};

const mapGhGasEmissionVerifyToPayload = (
  ghGasEmissionVerify: IGhGasEmissionVerifyFormValues
): IGhGasEmissionVerify => {
  const { verifier, remark, ...values } = ghGasEmissionVerify;
  return {
    ...values,
    verifier:
      verifier.infoCategoryMasterDataId !== ''
        ? {
            ...verifier,
            nameTh:
              verifier.otherNameTh && verifier.otherNameTh !== ''
                ? verifier.otherNameTh
                : null,
            nameEn:
              verifier.otherNameEn && verifier.otherNameEn !== ''
                ? verifier.otherNameEn
                : null,
          }
        : null,
    remark: mapRemarkToPayload(remark),
  } as IGhGasEmissionVerify;
};

const mapPerformanceGhGasMgmtToPayload = (
  performanceGhGasMgmt?: IPerformanceGhGasMgmtFormValues
): IPerformanceGhGasMgmt => {
  const { remark, assets, ...rest } = performanceGhGasMgmt || {};

  return {
    ...rest,
    remark: mapRemarkToPayload(remark),
    assets: mapAssets(assets),
  } as IPerformanceGhGasMgmt;
};

const mapGreenhouseGoalTermToPayload = (
  greenhouseGoalTerm?: IGreenhouseGoalTermFormValues
): IGreenhouseGoalTerm => {
  const {
    introduction,
    remark,
    greenhouseGoal,
    carbon,
    netZero,
    other,
    ...value
  } = greenhouseGoalTerm || {};

  return {
    ...value,
    introduction: mapIntroToPayload(introduction),
    remark: mapRemarkToPayload(remark),
    greenhouseGoal: mapGreenhouseGoalToPayload(greenhouseGoal),
    netZero: mapNetZeroToPayload(netZero),
    carbon: mapCarbonToPayload(carbon),
    other: mapOtherToPayload(other),
  } as IGreenhouseGoalTerm;
};

const mapGreenhouseGoalToPayload = (
  greenhouseGoal?: IGreenhouseGoalFormValues
): IGreenhouseGoal => {
  if (!greenhouseGoal) {
    return {
      goals: [],
    };
  }

  const { goals, remark, ...values } = greenhouseGoal;
  const goalTypes: Array<GoalType> = [];
  if (goals.netZero) goalTypes.push(GoalType.NET_ZERO);
  if (goals.carbon) goalTypes.push(GoalType.CARBON);
  if (goals.other) goalTypes.push(GoalType.OTHER);

  return {
    ...values,
    remark: mapRemarkToPayload(remark),
    goals: goalTypes,
  } as IGreenhouseGoal;
};

const mapNetZeroToPayload = (netZero?: INetZeroFormValues): INetZero => {
  const { remark, details = [], ...values } = netZero || {};

  return {
    ...values,
    remark: mapRemarkToPayload(remark),
    details: details.map((detail) => ({
      ...detail,
      baseYear: mapTermTargetToPayload(detail.baseYear),
      nearTermTarget: mapTermTargetToPayload(detail.nearTermTarget),
      longTermTarget: mapTermTargetToPayload(detail.longTermTarget),
      tgoId: detail.tgoId ?? null,
      sbtiId: detail.sbtiId ?? null,
    })),
  } as INetZero;
};

const mapCarbonToPayload = (carbon?: ICarbonFormValues): ICarbon => {
  const { remark, details = [], ...values } = carbon || {};

  return {
    ...values,
    remark: mapRemarkToPayload(remark),
    details: details.map((detail) => ({
      ...detail,
      baseYear: mapTermTargetToPayload(detail.baseYear),
      target: mapTermTargetToPayload(detail.target),
      organizationId: detail.organizationId ?? null,
    })),
  } as ICarbon;
};

const mapOtherToPayload = (
  other?: IGreenhouseGasOtherFormValues
): IGreenhouseGasOther => {
  const { remark, details = [], ...values } = other || {};

  return {
    ...values,
    remark: mapRemarkToPayload(remark),
    details: details.map((detail) => ({
      ...detail,
      scopeId: detail.scopeId,
      baseYear: mapTermTargetToPayload(detail.baseYear),
      nearTermTarget: mapTermTargetToPayload(detail.nearTermTarget),
      longTermTarget: mapTermTargetToPayload(detail.longTermTarget),
    })),
  };
};

const mapTermTargetToPayload = (
  termTarget?: ITermTargetFormValues
): ITermTarget => {
  const { year, gasEmissionQuantity, gasEmissionPercent } = termTarget || {};
  return {
    year: year ? year.toDate() : null,
    gasEmissionPercent: gasEmissionPercent ? Number(gasEmissionPercent) : null,
    gasEmissionQuantity: gasEmissionQuantity
      ? Number(gasEmissionQuantity)
      : null,
  } as ITermTarget;
};

const mapGhStrategyToPayload = (
  greenhouseStrategy?: IGreenhouseStrategyFormValues
) => {
  const { introduction, remark, plan, ...value } = greenhouseStrategy || {};

  return {
    ...value,
    introduction: mapIntroToPayload(introduction),
    remark: mapRemarkToPayload(remark),
    plan: {
      ...plan,
      remark: mapRemarkToPayload(plan?.remark),
    },
  };
};

export const mapClimateChangeStructureToPayload = (
  data?: IClimateChangeStructureFormValues
) => {
  const {
    introduction,
    remark,
    boardResponsibility,
    subcommitteeResponsibility,
    committee,
    ...value
  } = data || {};

  const mappedCommittee = [
    ...(committee?.boardResponsibility
      ? [CommitteeType.BOARD_OF_DIRECTOR]
      : []),
    ...(committee?.subcommitteeResponsibility
      ? [CommitteeType.SUBCOMMITTEE]
      : []),
  ];

  return {
    ...value,
    introduction: mapIntroToPayload(introduction),
    remark: mapRemarkToPayload(remark),
    committee: mappedCommittee,
    boardResponsibility:
      boardResponsibility && mapBoardToPayload(boardResponsibility),
    subcommitteeResponsibility: mapSubcommitteeToPayload(
      subcommitteeResponsibility
    ),
  };
};

const mapSubcommitteeToPayload = (
  subcommitteeResponsibility?: ISubcommitteeResponsibilityFormValues
) => {
  const {
    remark,
    subcommittees = [],
    ...values
  } = subcommitteeResponsibility || {};
  let newSubcommittees = null;

  if (subcommittees) {
    newSubcommittees = subcommittees.map((subcommittee) => {
      const { charterPageEn, charterPageTh, charterUrlEn, charterUrlTh } =
        subcommittee;
      const hasValidOtherRoles = subcommittee.otherSubcommitteeRoles?.some(
        (role) => role.nameTh || role.nameEn || role.infoCategoryMasterDataId
      );
      return {
        ...subcommittee,
        charterPageEn:
          charterPageEn && charterPageEn !== '' ? charterPageEn : null,
        charterPageTh:
          charterPageTh && charterPageTh !== '' ? charterPageTh : null,
        charterUrlEn: charterUrlEn && charterUrlEn !== '' ? charterUrlEn : null,
        charterUrlTh: charterUrlTh && charterUrlTh !== '' ? charterUrlTh : null,
        otherSubcommitteeRoles: hasValidOtherRoles
          ? subcommittee.otherSubcommitteeRoles
          : null,
      };
    });
  }

  return {
    ...values,
    remark: mapRemarkToPayload(remark),
    subcommittees: newSubcommittees,
  } as ISubcommitteeResponsibility;
};

const mapBoardToPayload = (
  boardResponsibility: IBoardResponsibilityFormValues
) => {
  if (Object.keys(boardResponsibility).length === 0) {
    return null;
  }
  return {
    ...boardResponsibility,
    remark: mapRemarkToPayload(boardResponsibility.remark),
  };
};

const mapRemarkToPayload = (remark?: RemarkFormValues): IRemark | null => {
  if (!remark) return null;

  const { id, descriptionTh, descriptionEn } = remark;

  return descriptionTh || descriptionEn
    ? {
        id,
        descriptionTh: descriptionTh ?? null,
        descriptionEn: descriptionEn ?? null,
      }
    : null;
};

const mapIntroToPayload = (introduction?: RemarkFormValues): IRemark | null => {
  if (!introduction) return null;

  const { id, descriptionTh, descriptionEn } = introduction;

  return descriptionTh || descriptionEn
    ? {
        id,
        descriptionTh: descriptionTh ?? null,
        descriptionEn: descriptionEn ?? null,
      }
    : null;
};
